import type { WorkflowNodeData } from '@/types/workflow'

interface AiDecisionConfigProps {
  data: WorkflowNodeData
  updateField: (field: string, value: any) => void
}

import { inputClass, inputStyle, sectionClass, infoBannerClass, textareaClass } from './_kit'
import { Field } from '@/components/ui/Field'
import { cn } from '@/lib/utils'
interface AiBranch {
  id: string
  label: string
  description: string
}

const DEFAULT_CONFIG = {
  decision_prompt: '',
  branches: [{ id: 'branch_1', label: 'Branch 1', description: '' }] as AiBranch[],
  contact_fields: [] as string[],
}

export default function AiDecisionConfig({ data, updateField }: AiDecisionConfigProps) {
  const config = (data.ai_decision_config || DEFAULT_CONFIG) as {
    decision_prompt: string
    branches: AiBranch[]
    contact_fields: string[]
  }

  const updateConfig = (updates: Partial<typeof config>) => {
    updateField('ai_decision_config', { ...config, ...updates })
  }

  const updateBranch = (branchId: string, field: string, value: string) => {
    const updatedBranches = config.branches.map((b) =>
      b.id === branchId ? { ...b, [field]: value } : b
    )
    updateConfig({ branches: updatedBranches })
  }

  const addBranch = () => {
    if (config.branches.length >= 5) return
    const newBranch: AiBranch = {
      id: `branch_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      label: `Branch ${config.branches.length + 1}`,
      description: '',
    }
    updateConfig({ branches: [...config.branches, newBranch] })
  }

  const removeBranch = (branchId: string) => {
    updateConfig({ branches: config.branches.filter((b) => b.id !== branchId) })
  }

  return (
    <div className={sectionClass}>
      <Field label="Label">
        {(f) => (
          <input
            {...f}
            type="text"
            className={inputClass} style={inputStyle}
            value={data.label || ''}
            onChange={(e) => updateField('label', e.target.value)}
            placeholder="AI Decision"
          />
        )}
      </Field>

      <Field label="Decision Prompt">
        {(f) => (
          <textarea
            {...f}
            className={cn(textareaClass, 'h-[96px] overflow-y-auto')} style={inputStyle}
            value={config.decision_prompt}
            onChange={(e) => updateConfig({ decision_prompt: e.target.value })}
            placeholder="Based on the contact's profile and engagement, decide which path..."
            rows={4}
          />
        )}
      </Field>

      <Field label="Branches" as="group" className="space-y-3">
        {config.branches.map((branch) => (
          <div
            key={branch.id}
            className="p-3 bg-muted rounded-xl space-y-2 relative"
          >
            <div className="flex items-center gap-2">
              <input
                type="text"
                className={inputClass} style={inputStyle}
                value={branch.label}
                onChange={(e) => updateBranch(branch.id, 'label', e.target.value)}
                placeholder="Branch label"
              />
              <button
                onClick={() => removeBranch(branch.id)}
                className="p-1.5 text-muted-foreground hover:text-red-500 hover:bg-red-500/15 rounded-lg transition-all duration-150 flex-shrink-0"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <input
              type="text"
              className={inputClass} style={inputStyle}
              value={branch.description}
              onChange={(e) => updateBranch(branch.id, 'description', e.target.value)}
              placeholder="Description (helps AI understand this branch)"
            />
          </div>
        ))}

        {config.branches.length < 5 && (
          <button
            onClick={addBranch}
            className="w-full px-3 py-2 border border-dashed border-border/60 rounded-lg text-sm text-muted-foreground hover:border-primary/60 hover:text-primary transition-all duration-150"
          >
            + Add Branch
          </button>
        )}
      </Field>

      <div className={infoBannerClass}>
        <p className="text-xs">
          Uses AI to analyze contact data and route to the best branch. Token usage will be tracked.
        </p>
      </div>
    </div>
  )
}
